import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { RejectionBadge, StatusBadge } from '../../shared/agentVerificationStatusBadges'

import { useValidationSubmissionsAccess } from '@percent/admin-dashboard/common/hooks'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { ButtonText, DocumentsGallery, Flex, Heading, Modal, Typography } from '@percent/lemonade'
import { useQuery, useQueryList } from '@percent/utility'
import styles from './AgentVerificationDocumentComparisonModal.module.scss'
import { AgentVerificationDocumentComparisonModalProps } from './AgentVerificationDocumentComparisonModal.types'

export function AgentVerificationDocumentComparisonModal({
  onClose,
  thisApplication,
  thisApplicationDocuments,
  matches,
  initialMatchIndex
}: Readonly<AgentVerificationDocumentComparisonModalProps>) {
  const { t } = useTranslation()
  const { adminService, agentVerificationService } = useServices()
  const { canReadDocuments } = useValidationSubmissionsAccess()
  const [currentIndex, setCurrentIndex] = useState(initialMatchIndex)

  const currentMatch = matches[currentIndex]

  const { data: matchedAgentVerification } = useQuery(
    ['/agent-verifications', currentMatch?.id],
    agentVerificationService.getAgentVerification,
    { id: currentMatch?.id ?? '' },
    { enabled: !!currentMatch?.id }
  )

  const [{ data: matchedDocuments, isLoading: isLoadingMatchedDocuments }] = useQueryList(
    ['/validation-submission-documents', matchedAgentVerification?.validationSubmissionId],
    adminService.getValidationSubmissionDocuments,
    { validationSubmissionId: matchedAgentVerification?.validationSubmissionId ?? '' },
    { enabled: !!matchedAgentVerification?.validationSubmissionId && canReadDocuments }
  )

  const matchedDocumentIds = new Set(currentMatch?.documentIds ?? [])
  const matchedDocument = matchedDocuments?.find(document => matchedDocumentIds.has(document.id)) ?? null
  const thisDocument = thisApplicationDocuments?.[0] ?? null

  const matchedName = matchedAgentVerification
    ? `${matchedAgentVerification.firstName} ${matchedAgentVerification.lastName}`
    : currentMatch?.email ?? ''

  return (
    <Modal open onClose={onClose} width={920} data-testid="agent-verification-document-comparison-modal">
      <div className={styles.content}>
        <Flex align="center" mb={24} justify="space-between">
          <Heading level="h4">{t('typography.documentComparison.title')}</Heading>
          <ButtonText icon="close" onClick={onClose} aria-label={t('typography.documentComparison.close')} />
        </Flex>

        <Flex direction={{ base: 'column', lg: 'row' }} gap={24} align="start">
          <Flex direction="column" gap={4} flex={1} miw={0}>
            <Typography>{t('typography.documentComparison.thisApplication')}</Typography>
            <Flex align="center" gap={8}>
              <Typography>
                <strong>{thisApplication.name}</strong>
              </Typography>
              {thisApplication.status && <StatusBadge status={thisApplication.status} />}
            </Flex>
            {thisApplication.email && <Typography>{thisApplication.email}</Typography>}
            {thisApplication.organisationName && <Typography>{thisApplication.organisationName}</Typography>}
            <div className={styles.documentPreview}>
              {thisDocument ? (
                <DocumentsGallery files={[{ url: thisDocument.url }]} withPreview previewUrl="/document-preview" />
              ) : (
                <Typography>{t('typography.documentComparison.noDocument')}</Typography>
              )}
            </div>
          </Flex>

          <Flex direction="column" gap={4} flex={1} miw={0}>
            <Typography>{t('typography.documentComparison.matchedApplication')}</Typography>
            <Flex align="center" gap={8}>
              <Typography>
                <strong>{matchedName}</strong>
              </Typography>
              {currentMatch?.status && <StatusBadge status={currentMatch.status} />}
              {currentMatch?.rejectionReasonCode && (
                <RejectionBadge rejectionReasonCode={currentMatch.rejectionReasonCode} />
              )}
            </Flex>
            {currentMatch?.email && <Typography>{currentMatch.email}</Typography>}
            {currentMatch?.organisationName && <Typography>{currentMatch.organisationName}</Typography>}
            <div className={styles.documentPreview}>
              {matchedDocument ? (
                <DocumentsGallery
                  key={matchedDocument.url}
                  files={[{ url: matchedDocument.url }]}
                  withPreview
                  previewUrl="/document-preview"
                />
              ) : (
                <Typography>
                  {isLoadingMatchedDocuments
                    ? t('typography.documentComparison.loadingDocument')
                    : t('typography.documentComparison.noDocument')}
                </Typography>
              )}
            </div>
          </Flex>
        </Flex>

        {matches.length > 1 && (
          <Flex align="center" mt={24} justify="space-between">
            <ButtonText
              icon="arrow-left"
              disabled={currentIndex <= 0}
              onClick={() => setCurrentIndex(index => index - 1)}
            >
              {t('typography.documentComparison.previous')}
            </ButtonText>
            <ButtonText
              icon="arrow-right"
              disabled={currentIndex >= matches.length - 1}
              onClick={() => setCurrentIndex(index => index + 1)}
            >
              {t('typography.documentComparison.next')}
            </ButtonText>
          </Flex>
        )}
      </div>
    </Modal>
  )
}
